import { useState, useEffect } from 'react';
import {
  Autocomplete,
  OnSelectChange,
  SelectSize,
  IconClose,
  SelectItemProps,
} from '@ui/src';
import useGetAutocompleteList from '../../../store/dictionaries/helpers/use-sic-codes-autocomplete';
import InfoRow from '../info-row';
import { useLazyGetCustomersSicCodesQuery } from '../../../store/dictionaries/services';
import { SicCodesTypes } from '../types';

import cn from '../new-application.module.css';

const SicCodes = ({
  value,
  setValue,
  setValuesList,
  index,
  values,
  disabled,
  error,
  clearErrors,
}: SicCodesTypes) => {
  const [sicCodeErrorVisible, setSicCodeErrorVisible] = useState(false);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const {
    onSearch,
    isLoading,
    pagination,
    searchQuery,
    setAutocompleteList,
    autocompleteList,
    setPagination,
  } = useGetAutocompleteList();

  const [getList] = useLazyGetCustomersSicCodesQuery();

  const onChange = (e: OnSelectChange) => {
    if (!values.find((item) => item.id === e.target.value)) {
      const nextValues = values.map((item, valueIndex) => {
        const result = { ...item };

        if (index === valueIndex) {
          result.name = e.target.name;
          result.id = e.target.value as string;
        }

        return result;
      });

      setValue(
        'sicCodes',
        nextValues.map((item) => item.id),
      );
      setValuesList(nextValues);
      setSicCodeErrorVisible(false);
    } else {
      setSicCodeErrorVisible(true);
    }
  };

  const onInfinateScroll = async () => {
    if (!nextPageIsLoading) {
      setNextPageIsLoading(true);

      const response = await getList(
        `?take=40&cursor=${pagination?.endCursor}${searchQuery || ''}`,
      );

      if (response && response.data && response.data.data) {
        const nextAutocompleteList = ([] as SelectItemProps[]).concat(
          autocompleteList,
        );

        response.data.data.edges?.forEach((item) => {
          nextAutocompleteList.push({
            name: item.node.code,
            value: item.node.code,
            description: item.node.description,
            suffix: `Risk score: ${item.node.riskScore}`,
          });
        });

        setAutocompleteList(nextAutocompleteList);
        setPagination(response.data.data?.pageInfo);

        setTimeout(() => {
          setNextPageIsLoading(false);
        }, 100);
      }
    }
  };

  const deleteSicCode = (id: string) => {
    let nextValues;

    if (id) {
      nextValues = values.filter((item) => item.id !== id);
    } else {
      nextValues = values.filter((item, itemIndex) => index !== itemIndex);
    }

    setValue(
      'sicCodes',
      nextValues.map((item) => item.id),
    );

    setValuesList(nextValues);
  };

  useEffect(() => {
    if (
      !autocompleteList.find((item) => item.value === value.id) &&
      value.name &&
      !value.name.includes('sicCodes')
    ) {
      onSearch(value.name, true);
    }
  }, [value, autocompleteList]);

  return (
    <InfoRow title="Sic codes" id="sicCodes" borderless lessPadding>
      <>
        <div className={cn.sicCodeRow}>
          <Autocomplete
            name={`sicCodes-${index}`}
            placeholder="Select values"
            value={value.id}
            onChange={(e) => {
              onChange(e);
              clearErrors(['sicCodes']);
            }}
            className={`${cn.newApplicationAutocomplete} ${
              disabled ? cn.newApplicationAutocompleteDisabled : ''
            }`}
            size={SelectSize.SMALL}
            isLoading={isLoading}
            onSearch={onSearch}
            list={autocompleteList}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onInfinateScroll={onInfinateScroll}
            hasNextPage={pagination?.hasNextPage}
            nextPageIsLoading={nextPageIsLoading}
            error={error}
            valueWithDescription
            withInfiniteScroll
            borderless
            fullWidth
          />
          {!disabled && (
            <button
              type="button"
              className={cn.deleteSicCodeButton}
              onClick={() => deleteSicCode(value.id)}
              aria-label="Delete button"
              tabIndex={-1}
            >
              <IconClose width={15} height={15} />
            </button>
          )}
        </div>
        {sicCodeErrorVisible && (
          <div className={cn.sicCodesError}>Sic Codes must be unique</div>
        )}
      </>
    </InfoRow>
  );
};

export default SicCodes;
